import {Button, Dialog, DialogActions, DialogContent, useMediaQuery, IconButton, InputAdornment, Link, MenuItem, Tooltip, Typography} from "@mui/material";
import React, {useEffect, useState} from "react";
import {Visibility, VisibilityOff } from "@mui/icons-material";
import { SessionContext } from "../../util/SessionContext";
import css_self from "./css/LoginMain.module.scss";
import css_common from "./css/LoginCommon.module.scss";
import css_help from "./css/LoginHelp.module.scss";
import css_buttons from "../Common/css/CCButton.module.scss";
import parkourImage from "../../assets/ParkourLogo.svg";
import CCInputLabel from "../Common/CCInputLabel";
import CCTextField from "../Common/CCTextField";
import CCPulse from "../Common/CCPulse";
import CCSelect from "../Common/CCSelect";
import PersonaSelect from "./PersonaSelect";
import { Blue3 } from "../../theme/styles";
import { sleep } from "../../util/Sleep";
import axios from "axios";
import {Link as RouterLink} from "react-router";
import {initiateSso} from "../../data/oidcSso";
import {postLoginRedirect} from "../../util/postLoginRedirect";
import * as validators from "../../util/Validators";
import * as formatters from "../../util/Formatters";
import Countdown, {zeroPad} from "react-countdown";
import {accountHoldsPersona} from "../../util/ssoAuthenticate";

async function login(preSessionState, setPreSessionState, sessionContext) {
    const PREAUTH_KEY = "t1.preauth.v3";

    async function mintPreAuthToken() {
        const preAuthResponse = await axios({
            method: "GET",
            baseURL: process.env.REACT_APP_API_URL,
            url: "t1/v1/authenticate",
            params: {
                Type: "preauth",
            },
        });

        localStorage.setItem(PREAUTH_KEY, preAuthResponse.data.Token);
        return preAuthResponse.data.Token;
    }

    const authenticate = (preAuthToken) => axios({
        method: "POST",
        baseURL: process.env.REACT_APP_API_URL,
        url: "t1/v1/authenticate",
        json: true,
        headers: {
            "X-Cres-AccessToken": preAuthToken,
        },
        data: {
            Visibility: preSessionState.visibility,
            Email: preSessionState.email,
            Password: preSessionState.password,
            ...(preSessionState.secureLogin?.secureCode ? {
                SecureCode: preSessionState.secureLogin.secureCode,
                RememberDevice: preSessionState.secureLogin.rememberDevice,
            } : {}),
        },
    }).catch(e => e.response || e);

    let response = await authenticate(localStorage.getItem(PREAUTH_KEY) || await mintPreAuthToken());

    // The cached token is never checked before it is sent, and it never expires. If the edge
    // refuses it the request never reaches the tier, so the failure arrives with no body and
    // nothing to explain it. Discard it and mint once, rather than wedging login until someone
    // clears site data by hand.
    if (!(response instanceof Error) && (response.status === 401 || response.status === 403)) {
        localStorage.removeItem(PREAUTH_KEY);
        response = await authenticate(await mintPreAuthToken());
    }

    if (response instanceof Error || response.status !== 200) {
        return response;
    }

    // multiple account
    if (response.data.Code === "CC-ADC7-49A2" || response.data.Code === "CC-E5EB-43D5") {
        return response;
    }

    // change initial password
    if (response.data.Code === "CC-240C-4072") {
        setPreSessionState({
            accessToken: response.data["AccessToken"],
        });

        return response;
    }

    let visibilityMode, visibilityValue
    // A HANDOFF'S PERSONA IS A PREFERENCE, NOT AN OVERRIDE. It names the persona the SOURCE
    // application was being used under, and a person with two accounts can arrive here having been
    // logged into the other one -- so the hint names a position in Peak Medical while this session
    // belongs to Alpha Medical. Taken unconditionally, as it was, that produces a session whose
    // every read is refused CC-PRSN-DENY at 403 and an app reporting an unexpected system error.
    // Measured in prod on 2026-10-17. See accountHoldsPersona.
    const handoff = preSessionState.visibility
        ? preSessionState.visibility.split(":")
        : [];
    if (handoff.length === 3 && accountHoldsPersona(response.data["Account"], handoff[2])) {
        [, visibilityMode, visibilityValue] = handoff;
    } else if (response.data["Account"]["Positions"].length > 0) {
        visibilityMode = "Position";
        visibilityValue = response.data["Account"]["Positions"][0].USID;
    } else if (response.data["Account"]["UserGroups"].length > 0) {
        visibilityMode = "UserGroup";
        visibilityValue = response.data["Account"]["UserGroups"][0].USID;
    } else if (response.data["Account"]["WorkGroups"].length > 0) {
        visibilityMode = "WorkGroup";
        visibilityValue = response.data["Account"]["WorkGroup"][0].USID;
    }

    sessionContext.setIdentityToken(response.data["IdentityToken"])
    sessionContext.setSessionUSID(response.data["Session"])
    sessionContext.setAccessToken(response.data["AccessToken"])
    sessionContext.setRefreshToken(response.data["RefreshToken"])
    sessionContext.setActiveVisibility(visibilityMode, visibilityValue)
    sessionContext.setAccountName(response.data["Account"]["AccountName"])
    sessionContext.setAccountUserUSID(response.data["Account"]["AccountUserUSID"])
    sessionContext.setAccount(response.data["Account"])
    sessionContext.setRoles(response.data["Roles"])
    sessionContext.setPrivileges(response.data.Privileges)
    sessionContext.setProducts(response.data.Products || [])
    sessionContext.setPrivilegeConstraints(response.data.PrivilegeConstraints)
    sessionContext.setTenants(response.data["Tenants"])
    sessionContext.setShowLoginDialog(false)
    sessionContext.setPickValues(response.data["PickValues"])

    // LAND ON THE DASHBOARD. Ed, 2026-10-28: "The app gives an error when it lands on the project
    // screen. It should land on the Dashboard." A tab that was parked on a deep link -- a record
    // the persona you just signed in as cannot see -- opened on that record's refusal. A gated entry
    // point may still stash a destination and win; otherwise a sign-in starts at the start.
    const destination = postLoginRedirect("/");
    if (destination) window.location.href = destination;

    return response;
}

export default function Login2() {
    const sessionContext = new SessionContext();

    const [preSessionState, setPreSessionState] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [errorText, setErrorText] = useState(undefined);
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [ssoError, setSsoError] = useState(null);
    const [passwordConfirm, setPasswordConfirm] = useState("");
    const [showPassword, setShowPassword] = useState("");
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [resentAt, setResentAt] = useState(undefined);
    const [verificationCode, setVerificationCode] = useState("");

    const [helpState, setHelpState] = useState({});
    const [accounts, setAccounts] = useState([]);
    const [displayedAccount, setDisplayedAccount] = useState("");
    const [displayedAccounts, setDisplayedAccounts] = useState([]);
    const [displayedVisibility, setDisplayedVisibility] = useState(null);

    const [loginState, setLoginState] = useState("login")
    const smallerWidth = useMediaQuery("(max-width:320px)");

    useEffect(() => {
        setPreSessionState({
            email: email,
            password: password,
            visibility: displayedVisibility
        })
    }, [email, password, displayedVisibility]);

    function default_visibility(accounts, account_name) {
        if (Object.keys(accounts).length > 0 && account_name in accounts) {

            const account = accounts[account_name];
            const accountUSID = account["AccountUSID"];

            if (account["Positions"].length > 0) {
                const position = account["Positions"][0];
                return `${accountUSID}:Position:${position["USID"]}`;
            }

            if (account["UserGroups"].length > 0) {
                const userGroup = account["UserGroups"][0];
                return `${accountUSID}:UserGroup:${userGroup["USID"]}`;
            }

            if (account["WorkGroups"].length > 0) {
                const workgroup = account["WorkGroups"][0];
                return `${accountUSID}:WorkGroup:${workgroup["USID"]}`;
            }
        }

        return null;
    }

    async function submit() {
        if (loginState === "login" || loginState === "postChangePassword") {
            const startAt = Date.now();

            const waitVisualMin = async () => {
                const elapsed = Date.now() - startAt;
                const remaining = 2000 - elapsed;
                if (remaining > 0) await sleep(remaining);
            };

            setSubmitting(true);
            setErrorText(undefined);

            // login() does a long sequence of sessionContext.setX(...) calls and closes this dialog
            // at the very END. Anything that throws part-way -- a claim the token does not carry, an
            // Account field the response omits -- used to escape as an unhandled rejection: the
            // spinner never cleared, no error was shown, and setShowLoginDialog(false) never ran, so
            // the login pane just sat there. Fail visibly instead.
            let response;
            try {
                response = await login(preSessionState, setPreSessionState, sessionContext);
            } catch (e) {
                console.error("login failed", e);
                setSubmitting(false);
                setErrorText("We could not complete your sign in. Please try again.");
                return;
            }

            if (response instanceof Error && response.message === "Network Error") {
                setSubmitting(false);
                setErrorText("Connection not available");
                return;
            }

            // maintenance outage is in effect
            if (response.status === 503 && response.data.Code === "CC-CF78-4C2A") {
                window.location.reload();
                return;
            }

            if (response.status !== 200) {
                await waitVisualMin();

                setSubmitting(false);

                if (response.status === 429) {
                    if (response.data.Code === "CC-746A-4E73") {
                        setErrorText("Exceeded 5 failed login attempts in 24 hours");
                        return;
                    }
                }

                if (response.status === 400) {
                    if (response.data.Code === "CC-11C9-41C9") {
                        if (response.data["FailCount"] === 8) {
                            setErrorText("Invalid username or password. 8th consecutive failure. 2 more attempts allowed.");
                        } else if (response.data["FailCount"] === 9) {
                            setErrorText("Invalid username or password. 9th consecutive failure. 1 more attempt allowed.");
                        } else if (response.data["FailCount"] === 10) {
                            setErrorText("Invalid username or password. Account locked.");
                        } else {
                            setErrorText("Invalid username or password.");
                        }
                        return;
                    }

                    if (response.data.Code === "CC-1242-4AAC") {
                        setErrorText("User is deactivated. Please contact administrator.");
                        return;
                    }
                    if (response.data.Code === "CC-1242-4WWC") {
                        setErrorText("User has not confirmed their account. Please contact administrator.");
                        return;
                    }
                }

                if (response.status === 401 || response.status === 403) {
                    setErrorText("Invalid username or password");
                } else {
                    setErrorText("Service temporarily unavailable");
                }
            }

            // multiple accounts
            if (response.data.Code === "CC-ADC7-49A2" || response.data.Code === "CC-E5EB-43D5") {
                setSubmitting(false);

                const accounts = response.data["Accounts"];

                const displayedAccounts = Object.keys(accounts).sort();
                const displayedAccount = displayedAccounts[0];
                const displayedVisibility = default_visibility(accounts, displayedAccount);

                setAccounts(accounts);

                setDisplayedAccount(displayedAccount);
                setDisplayedAccounts(displayedAccounts);
                setDisplayedVisibility(displayedVisibility);

                setShowPassword(false);
                return;
            }

            if (response.data.Code === "CC-240C-4072") {
                sessionContext.setAccessToken(response.data["AccessToken"])
                setLoginState("changeInitialPassword");
                setSubmitting(false);
                setErrorText(undefined);
                setPassword("");
                setShowPassword(false);
                setPasswordConfirm("");
                return;
            }

            setPreSessionState({});
            setSubmitting(false)
            setErrorText(undefined);
            setEmail("");
            setPassword("");
            setShowPassword(false);
            setAccounts([]);
            setDisplayedAccount("");
            setDisplayedAccounts([]);
            setDisplayedVisibility(null);
        }
        else if (loginState === "help1") {
            const startAt = Date.now();

            setSubmitting(true);

            const response = await axios({
                method: "POST",
                baseURL: process.env.REACT_APP_API_URL,
                url: "t1/v1/forgot_password_send_code",
                json: true,
                data: {
                    Email: email,
                },
            }).catch(e => e.response || e);

            if (response instanceof Error && response.message === "Network Error") {
                setSubmitting(false);
                setErrorText("Connection not available");
                return;
            }

            const waitDiff = 2000 - (Date.now() - startAt);
            waitDiff > 0 && (await sleep(waitDiff));

            if (response.status !== 200) {
                setSubmitting(false);
                setErrorText("Service temporarily unavailable");
                return;
            }

            setHelpState({
                key: response.data.Key,
                email: email,
            });

            setSubmitting(false);
            setLoginState("help2");
        }
        else if (loginState === "help2") {
            const startAt = Date.now();

            setSubmitting(true);

            const response = await axios({
                method: "GET",
                baseURL: process.env.REACT_APP_API_URL,
                url: "t1/v1/forgot_password_verify_code",
                params: {
                    Code: verificationCode,
                    Key: helpState.key,
                },
            }).catch(e => e.response || e);

            if (response instanceof Error && response.message === "Network Error") {
                setSubmitting(false);
                setErrorText("Connection not available");
                return;
            }

            const waitDiff = 2000 - (Date.now() - startAt);
            waitDiff > 0 && (await sleep(waitDiff));

            setSubmitting(false);

            if (response.status === 204) {
                setLoginState("help3");
                return;
            }

            if (response.data.Code === "CC-C91C-61B3") {
                setErrorText("Expired verification code");
                return;
            }

            if (response.data.Code === "CC-A46A-453D") {
                setErrorText("Incorrect verification code");
                return;
            }

            setErrorText("Service temporarily unavailable");

            setSubmitting(false);
        }
        else if (loginState === "help3") {
            const startAt = Date.now();

            setSubmitting(true);

            const response = await axios({
                method: "POST",
                baseURL: process.env.REACT_APP_API_URL,
                url: "t1/v1/forgot_password",
                json: true,
                data: {
                    NewPassword: password,
                    Key: helpState.key,
                    Code: helpState.code,
                },
            }).catch(e => e.response || e);

            if (response instanceof Error && response.message === "Network Error") {
                setSubmitting(false);
                setErrorText("Connection not available");
                return;
            }

            const waitDiff = 2000 - (Date.now() - startAt);
            waitDiff > 0 && (await sleep(waitDiff));

            setSubmitting(false);
            if (response.status === 204) {
                setLoginState("login");
                setEmail("");
                setPasswordConfirm("");
                setVerificationCode("");
                setPassword("");
                return;
            }
            setErrorText("Service temporarily unavailable");
        }
        else if (loginState === "changeInitialPassword") {
            setSubmitting(true);

            const response = await axios({
                method: "POST",
                baseURL: process.env.REACT_APP_API_URL,
                url: "t1/v1/authenticate_change_password",
                json: true,
                headers: {
                    "X-Cres-AccessToken": sessionContext.getAccessToken(),
                },
                data: {
                    Password: password,
                }
            }).catch(e => e.response || e);

            if (response instanceof Error && response.message === "Network Error") {
                setSubmitting(false);
                setErrorText("Connection not available");
                return;
            }

            setSubmitting(false);
            if (response.status === 204) {
                setLoginState("postChangePassword");
                await login(preSessionState, setPreSessionState, sessionContext);
                return;
            }

            setErrorText("Service temporarily unavailable");
        }
    }

    let loginFormToPresent = null;

    if (displayedAccounts.length === 0) {
        loginFormToPresent = (
            <>
                <Typography className={css_common.Title}>
                    Login to your account
                </Typography>

                <CCInputLabel>Email</CCInputLabel>
                <CCTextField value={email} placeholder="Enter your email" onChange={(e) => setEmail(e.target.value)} autoComplete="email"/>
                <CCInputLabel>Password</CCInputLabel>
                <CCTextField
                    value={password}
                    placeholder="Enter your password"
                    type={showPassword ? "text" : "password"}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="current-password"
                    error={!!errorText}
                    helperText={errorText || " "}
                    slotProps={{
                        input: {
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        onClick={() => setShowPassword(!showPassword)}
                                        onMouseDown={(e) => e.preventDefault()}
                                        edge="end">
                                        {showPassword ? <Visibility/> : <VisibilityOff/>}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        },
                    }}
                />

                <Button
                    className={css_buttons.blueStandardButton}
                    style={{width: "100%"}}
                    disabled={submitting || !email.trim() || !password.trim()}
                    onClick={submit}>
                    <span style={{visibility: submitting ? "hidden" : "visible"}}>
                        Login
                    </span>
                    {submitting && <CCPulse className={css_buttons.Pulse}/>}
                </Button>

                {/* Inbound SSO: use the entered email to discover the customer's IdP and
                    redirect to it. The IdP redirects back to oidc_callback → ?ssoAccessToken. */}
                <div style={{marginTop: "12px", textAlign: "center"}}>
                    <Link
                        component="button"
                        underline="hover"
                        disabled={submitting || !email.trim()}
                        onClick={async () => {
                            setSsoError(null);
                            try {
                                window.location.href = await initiateSso(email.trim());
                            } catch (e) {
                                setSsoError(e?.response?.data?.description ?? e?.message ?? "SSO is not available for this email.");
                            }
                        }}>
                        Sign in with SSO
                    </Link>
                    {ssoError && (
                        <Typography style={{color: "#d32f2f", fontSize: 12, marginTop: 6}}>{ssoError}</Typography>
                    )}
                </div>
            </>
        );
    }

    if (displayedAccounts.length >= 1) {
        const account = accounts[displayedAccount];
        const accountUSID = account["AccountUSID"];

        loginFormToPresent = (
            <>
                {displayedAccounts.length === 1 ? (
                    <Typography className={css_common.Title}>Select your persona</Typography>
                ) : (
                    <>
                        <Typography className={css_common.Title}>Select your account and persona</Typography>
                        <CCInputLabel>Account</CCInputLabel>
                        <CCSelect
                            size={"small"}
                            fullWidth={true}
                            value={displayedAccount}
                            onChange={e => {
                                setDisplayedAccount(e.target.value);
                                setDisplayedVisibility(default_visibility(accounts, e.target.value));
                            }}>
                            {displayedAccounts.map(account => (
                                <MenuItem value={account} key={`select-account-${account}`}>{account}</MenuItem>
                            ))}
                        </CCSelect>
                    </>
                )}
                <PersonaSelect
                    positions={account["Positions"]}
                    positionAncestors={account["PositionAncestors"] ?? []}
                    userGroups={account["UserGroups"]}
                    workGroups={account["WorkGroups"]}
                    valuePrefix={`${accountUSID}:`}
                    value={displayedVisibility}
                    onChange={setDisplayedVisibility}/>
                <Button
                    className={css_buttons.blueStandardButton}
                    style={{marginTop: "20px", width: "100%"}}
                    disabled={
                        submitting ||
                        (displayedAccounts.length > 0 && (displayedVisibility === null || displayedVisibility.length === 0))
                    }
                    onClick={() => {
                        setErrorText(undefined);
                        submit();
                    }}>
                    <span style={submitting ? {visibility: "hidden"} : undefined}>Select</span>
                    {submitting ? <CCPulse className={css_buttons.Pulse}/> : undefined}
                </Button>
            </>
        );
    }

    function validateEmail(value, text) {
        if (value.length === 0 || validators.IsEmail(value)) {
            if (text && errorText === text) {
                setErrorText(undefined);
            }
            return true;
        } else if (text && errorText === undefined) {
            setErrorText(text);
        }
        return false;
    }

    let content;

    if (loginState === "login") {
        content = (
            <Dialog maxWidth="md" open={sessionContext.getShowLoginDialog?.() ?? false} scroll="body">
                <DialogContent style={{padding: "0 16px"}}>
                    <div style={{display: "flex", flexDirection: "row", justifyContent: "center"}}>
                        <div>
                            <img className={css_self.CompanyLogo} src={parkourImage} alt="Parkour Logo"/>
                        </div>
                        <div style={{marginTop: "10px"}}>
                            <span style={{marginLeft: "57px", fontSize: 25, fontWeight: 400, color: Blue3}}>|&nbsp;&nbsp;ParkourAI&nbsp;&nbsp;|&nbsp;&nbsp;QMS</span>
                        </div>
                    </div>

                    {loginFormToPresent}

                    <Typography className={css_self.HelpText} align="center" mt={2}>
                        <Link variant="body2" color="inherit" onClick={() => setLoginState("help1")}>Trouble logging in?</Link>
                    </Typography>

                    {displayedAccounts.length > 0 ? (
                        <Typography className={css_self.HelpText}>
                            <Link variant="inherit" color="inherit" component={RouterLink} to="#" replace={true} onClick={() => {
                                sessionStorage.clear();
                                sessionContext.setShowLoginDialog(true);
                                setPreSessionState({});
                                setSubmitting(false)
                                setErrorText(undefined);
                                setEmail("");
                                setPassword("");
                                setShowPassword(false);
                                setAccounts([]);
                                setDisplayedAccount("");
                                setDisplayedAccounts([]);
                                setDisplayedVisibility(null);
                            }}>Start over</Link>
                        </Typography>
                    ) : null}
                </DialogContent>

                <DialogActions/>
            </Dialog>
        )
    }
    else if (loginState === "help1") {
        content = (
            <Dialog maxWidth="md" open={sessionContext.getShowLoginDialog?.() ?? false} scroll="body">
                <DialogContent style={{padding: "0 16px"}}>
                    <div style={{display: "flex", flexDirection: "row", justifyContent: "center"}}>
                        <div>
                            <img className={css_self.CompanyLogo} src={parkourImage} alt="Parkour Logo"/>
                        </div>
                        <div style={{marginTop: "10px"}}>
                            <span style={{marginLeft: "57px", fontSize: 25, fontWeight: 400, color: Blue3}}>|&nbsp;&nbsp;Parkour</span>
                        </div>
                    </div>

                    <Typography className={css_common.Title}>Let's get you into your account</Typography>
                    <CCInputLabel>Email address</CCInputLabel>
                    <CCTextField
                        value={email}
                        placeholder="Enter your email"
                        onValidate={e =>
                            validateEmail(
                                e.target.value,
                                "Invalid email address",
                            )
                        }
                        onChange={async e => setEmail(e.target.value)}
                        helperText={<span className="Mui-error" style={{visibility: errorText ? undefined : "hidden"}}>{errorText}</span>}
                    />
                    <Button
                        className={css_buttons.blueStandardButton}
                        style={{marginTop: "20px", width: "100%"}}
                        margin="none"
                        animate={submitting}
                        disabled={
                            submitting ||
                            email.length === 0 ||
                            !validateEmail(email)
                        }
                        onClick={() => {
                            setErrorText(undefined);
                            setHelpState("help1")
                            setResentAt(undefined);
                            submit();
                        }}>
                        <span style={submitting ? {visibility: "hidden"} : undefined}>Continue</span>
                        {submitting ? <CCPulse className={css_buttons.Pulse}/> : undefined}
                    </Button>
                    <Typography className={css_common.LoginInsteadText}>
                        Already have an account?{" "}
                        <Link onClick={() => setLoginState("login")}>Login</Link>
                    </Typography>

                </DialogContent>

                <DialogActions/>
            </Dialog>
        )
    }
    else if (loginState === "help2") {
        const resend = () =>
            axios({
                method: "POST",
                baseURL: process.env.REACT_APP_API_URL,
                url: "t1/v1/forgot_password_send_code",
                json: true,
                data: {
                    Email: email,
                },
            }).then(response => setHelpState({key: response.data.Key}));

        const emailMaxLength = smallerWidth ? 12 : 25;
        const displayValue = formatters.EmailWithEllipsis(email, emailMaxLength);

        content = (
            <Dialog maxWidth="md" open={sessionContext.getShowLoginDialog?.() ?? false} scroll="body">
                <DialogContent style={{padding: "0 16px"}}>
                    <div style={{display: "flex", flexDirection: "row", justifyContent: "center"}}>
                        <div>
                            <img className={css_self.CompanyLogo} src={parkourImage} alt="Parkour Logo"/>
                        </div>
                        <div style={{marginTop: "10px"}}>
                            <span style={{marginLeft: "57px", fontSize: 25, fontWeight: 400, color: Blue3}}>|&nbsp;&nbsp;Parkour</span>
                        </div>
                    </div>

                    <div style={{marginTop: "20px"}}>
                        <Typography className={css_help.InstructionText}>
                            We have sent the verification code to{" "}
                            {displayValue !== email ? (
                                <Tooltip arrow title={email}>
                                    <Typography data-test-value={email} component="span" className={css_help.Bolder}>{displayValue}</Typography>
                                </Tooltip>
                            ) : (
                                <Typography component="span" className={css_help.Bolder}>{displayValue}</Typography>
                            )}
                            . Please check your email and enter the code below.
                        </Typography>
                        <Typography className={css_help.RedoText}>
                            <Link variant="inherit" color="inherit" href="#" onClick={e => {
                                e.preventDefault();
                                setHelpState({
                                    key: undefined,
                                });
                                setVerificationCode("");
                                setResentAt(undefined);
                                setLoginState("help1")
                            }}>Edit the email address</Link>
                        </Typography>
                        {resentAt ? (
                            <Typography className={css_help.RedoDisabledText}>
                                Resend the code
                                <Countdown
                                    date={resentAt + 60000}
                                    onComplete={() => setResentAt(undefined)}
                                    renderer={({hours, minutes, seconds, completed}) => {
                                        return ` (${zeroPad(minutes * 60 + seconds)}s)`;
                                    }}
                                />
                            </Typography>
                        ) : (
                            <Typography className={css_help.RedoText}>
                                <Link variant="inherit" color="inherit" href="#" onClick={async e => {
                                    e.preventDefault();
                                    resend();
                                    setResentAt(Date.now());
                                }}>Resend the code</Link>
                            </Typography>
                        )}
                        <CCInputLabel className={css_help.MuiInputLabel}>Verification code</CCInputLabel>
                        <CCTextField
                            placeholder="Enter your verification code"
                            value={verificationCode}
                            onChange={async e => {
                                const pattern = /^([a-zA-Z]){0,8}$/i;
                                if (e.target.value === "" || pattern.test(e.target.value)) {
                                    setVerificationCode(e.target.value.toUpperCase());
                                }
                            }}
                            inputProps={{
                                className: css_help["MuiInputBase-input"],
                                maxLength: 8,
                            }}
                            helperText={errorText ? <span className="Mui-error">{errorText}</span> : undefined}
                        />
                        <Typography className={css_help.VerificationNoticeText}>Your verification code may take a few moments to arrive. Do not share this code with anyone.</Typography>
                        <Button
                            className={css_buttons.blueStandardButton}
                            style={{marginTop: "20px", width: "100%"}}
                            margin="none"
                            animate={submitting}
                            disabled={submitting || verificationCode.length !== 8}
                            onClick={() => {
                                setErrorText(undefined);
                                submit();
                            }}>
                            <span style={submitting ? {visibility: "hidden"} : undefined}>Continue</span>
                            {submitting ? <CCPulse className={css_buttons.Pulse}/> : undefined}
                        </Button>
                        <Typography className={css_common.LoginInsteadText}>
                            Already have an account?{" "}
                            <Link onClick={() => {
                                setVerificationCode("");
                                setLoginState("help3")
                            }
                            }>Login</Link>
                        </Typography>
                    </div>
                </DialogContent>

                <DialogActions/>
            </Dialog>
        )
    }
    else if (loginState === "help3") {
        content = (
            <Dialog maxWidth="md" open={sessionContext.getShowLoginDialog?.() ?? false} scroll="body">
                <DialogContent style={{ padding: "0 16px" }}>
                    <div style={{ display: "flex", flexDirection: "row", justifyContent: "center" }}>
                        <div>
                            <img className={css_self.CompanyLogo} src={parkourImage} alt="Parkour Logo" />
                        </div>
                        <div style={{ marginTop: "10px" }}>
                            <span style={{ marginLeft: "57px", fontSize: 25, fontWeight: 400, color: Blue3 }}>|&nbsp;&nbsp;Parkour</span>
                        </div>
                    </div>

                    <div style={{marginTop: "20px"}}>
                        <Typography className={css_common.Title}>Reset your password</Typography>
                        <CCInputLabel>New password</CCInputLabel>
                        <CCTextField
                            value={password}
                            placeholder="Enter new password"
                            type={showNewPassword ? "text" : "password"}
                            onChange={async e => setPassword(e.target.value)}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={async () => setShowPassword(!showPassword)}
                                            onMouseDown={e => e.preventDefault()}
                                            size="large"
                                        >
                                            {showNewPassword ? <Visibility /> : <VisibilityOff />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <CCInputLabel>Confirm new password</CCInputLabel>
                        <CCTextField
                            value={passwordConfirm}
                            placeholder="Reenter new password"
                            type={showNewPassword ? "text" : "password"}
                            onChange={async e => setPasswordConfirm(e.target.value)}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={async () => setShowNewPassword(!showNewPassword)}
                                            onMouseDown={e => e.preventDefault()}
                                            size="large"
                                        >
                                            {showNewPassword ? <Visibility /> : <VisibilityOff />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                            helperText={<span className="Mui-error" style={{visibility: errorText ? undefined : "hidden"}}>{errorText}</span>}
                        />
                        <Button
                            className={css_buttons.blueStandardButton}
                            style={{marginTop: "20px", width: "100%"}}
                            margin="none"
                            animate={submitting}
                            disabled={submitting || verificationCode.length !== 8}
                            onClick={() => {
                                setErrorText(undefined);
                                submit();
                            }}>
                            <span style={submitting ? {visibility: "hidden"} : undefined}>Reset Password</span>
                            {submitting ? <CCPulse className={css_buttons.Pulse}/> : undefined}
                        </Button>
                        <Typography className={css_common.LoginInsteadText}>
                            Already have an account?{" "}
                            <Link onClick={() => {
                                setVerificationCode("");
                                setLoginState("login")
                            }
                            }>Login</Link>
                        </Typography>
                    </div>
                </DialogContent>

                <DialogActions />
            </Dialog>
        )

    }
    else if (loginState === "changeInitialPassword") {
        content = (
            <Dialog maxWidth="md" open={sessionContext.getShowLoginDialog?.() ?? false} scroll="body">
                <DialogContent style={{ padding: "0 16px" }}>
                    <div style={{ display: "flex", flexDirection: "row", justifyContent: "center" }}>
                        <div>
                            <img className={css_self.CompanyLogo} src={parkourImage} alt="Parkour Logo" />
                        </div>
                        <div style={{ marginTop: "10px" }}>
                            <span style={{ marginLeft: "57px", fontSize: 25, fontWeight: 400, color: Blue3 }}>|&nbsp;&nbsp;Parkour</span>
                        </div>
                    </div>

                    <div style={{marginTop: "20px"}}>
                        <Typography className={css_common.Title}>Change initial password</Typography>
                        <CCInputLabel>New password</CCInputLabel>
                        <CCTextField
                            value={password}
                            placeholder="Enter new password"
                            type={showNewPassword ? "text" : "password"}
                            onChange={async e => setPassword(e.target.value)}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={async () => setShowPassword(!showPassword)}
                                            onMouseDown={e => e.preventDefault()}
                                            size="large"
                                        >
                                            {showNewPassword ? <Visibility /> : <VisibilityOff />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <CCInputLabel>Confirm new password</CCInputLabel>
                        <CCTextField
                            value={passwordConfirm}
                            placeholder="Reenter new password"
                            type={showNewPassword ? "text" : "password"}
                            onChange={async e => setPasswordConfirm(e.target.value)}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={async () => setShowNewPassword(!showNewPassword)}
                                            onMouseDown={e => e.preventDefault()}
                                            size="large"
                                        >
                                            {showNewPassword ? <Visibility /> : <VisibilityOff />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                            helperText={<span className="Mui-error" style={{visibility: errorText ? undefined : "hidden"}}>{errorText}</span>}
                        />
                        <Button
                            className={css_buttons.blueStandardButton}
                            style={{marginTop: "20px", width: "100%"}}
                            margin="none"
                            animate={submitting}
                            disabled={submitting || (password === null || passwordConfirm === null)}
                            onClick={() => {
                                setErrorText(undefined);
                                submit();
                            }}>
                            <span style={submitting ? {visibility: "hidden"} : undefined}>Change Password</span>
                            {submitting ? <CCPulse className={css_buttons.Pulse}/> : undefined}
                        </Button>
                        <Typography className={css_common.LoginInsteadText}>
                            Start over?{" "}
                            <Link onClick={() => {
                                setVerificationCode("");
                                setLoginState("login")
                            }
                            }>Login</Link>
                        </Typography>
                    </div>
                </DialogContent>

                <DialogActions />
            </Dialog>
        )

    }
    return content;
}